import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useI18n } from "../contexts/I18nContext";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { ShoppingBag, User, LogOut, LayoutDashboard, Menu, X } from "lucide-react";
import { Logo } from "./Logo";

export const MarketplaceLayout = ({ children }) => {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = React.useState(false);

  const dashLink = user?.role === "admin" ? "/admin" : user?.role === "artist" ? "/artist" : user?.role === "referrer" ? "/referrer" : "/account";

  return (
    <div className="min-h-screen bg-white text-neutral-950 font-sans">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/85 border-b border-black/5" data-testid="site-header">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <Logo variant="compact" />
          </Link>

          <nav className="hidden md:flex items-center gap-10 overline text-neutral-700">
            <NavLink to="/" end className={({ isActive }) => isActive ? "text-black" : "hover:text-black"} data-testid="nav-gallery">{t.nav.shop}</NavLink>
            <NavLink to="/sell" className={({ isActive }) => isActive ? "text-black" : "hover:text-black"} data-testid="nav-sell">{t.nav.sell}</NavLink>
            <NavLink to="/talleres" className={({ isActive }) => isActive ? "text-black" : "hover:text-black"} data-testid="nav-workshops">Talleres</NavLink>
          </nav>

          <div className="flex items-center gap-3 lg:gap-5">
            <button onClick={toggle} className="overline text-neutral-600 hover:text-black" data-testid="lang-toggle">
              {lang === "es" ? "EN" : "ES"}
            </button>
            <Link to="/cart" className="relative flex items-center gap-1.5 hover:text-black text-neutral-700" data-testid="nav-cart">
              <ShoppingBag size={18} strokeWidth={1.5} />
              {count > 0 && <span className="text-xs font-medium" data-testid="cart-count">{count}</span>}
            </Link>
            {user ? (
              <div className="hidden md:flex items-center gap-4">
                <Link to={dashLink} className="overline text-neutral-700 hover:text-black flex items-center gap-1.5" data-testid="nav-dashboard">
                  <LayoutDashboard size={16} strokeWidth={1.5} /> {t.nav.dashboard}
                </Link>
                <button onClick={() => { logout(); navigate("/"); }} className="overline text-neutral-700 hover:text-black flex items-center gap-1.5" data-testid="nav-logout">
                  <LogOut size={16} strokeWidth={1.5} /> {t.nav.logout}
                </button>
              </div>
            ) : (
              <div className="hidden md:flex items-center gap-4">
                <Link to="/login" className="overline text-neutral-700 hover:text-black" data-testid="nav-login">{t.nav.login}</Link>
                <Link to="/register" className="bg-black text-white px-5 py-2 overline hover:bg-neutral-800 transition-colors" data-testid="nav-register">{t.nav.register}</Link>
              </div>
            )}
            <button className="md:hidden" onClick={() => setMenuOpen(!menuOpen)} data-testid="mobile-menu-toggle">
              {menuOpen ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-black/5 bg-white px-6 py-4 flex flex-col gap-3 overline">
            <Link to="/" onClick={() => setMenuOpen(false)}>{t.nav.shop}</Link>
            <Link to="/sell" onClick={() => setMenuOpen(false)}>{t.nav.sell}</Link>
            <Link to="/talleres" onClick={() => setMenuOpen(false)}>Talleres</Link>
            {user ? (
              <>
                <Link to={dashLink} onClick={() => setMenuOpen(false)}>{t.nav.dashboard}</Link>
                <button onClick={() => { logout(); setMenuOpen(false); navigate("/"); }} className="text-left">{t.nav.logout}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMenuOpen(false)}>{t.nav.login}</Link>
                <Link to="/register" onClick={() => setMenuOpen(false)}>{t.nav.register}</Link>
              </>
            )}
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-black/5 mt-32 py-12">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid md:grid-cols-3 gap-8">
          <div>
            <Logo variant="stacked" />
            <p className="text-sm text-neutral-500 mt-6 max-w-xs">{t.hero.subtitle}</p>
            <p className="text-xs text-neutral-400 mt-4">
              Un proyecto de <a href="https://123imprime.com" target="_blank" rel="noreferrer" className="text-neutral-700 hover:text-black underline">123imprime</a> · d-impresión
            </p>
          </div>
          <div className="overline text-neutral-500 space-y-2">
            <div>Alicante · Online</div>
            <div>hello@d-pod.gallery</div>
            <div><Link to="/talleres" className="hover:text-black transition-colors" data-testid="footer-workshops">Talleres de enmarcación</Link></div>
            <div className="text-neutral-400 normal-case tracking-normal text-xs">Cada obra se entrega con certificado de autenticidad firmado por el artista.</div>
          </div>
          <div className="overline text-neutral-400 md:text-right">© {new Date().getFullYear()} d-Pod Gallery · 123imprime</div>
        </div>
      </footer>
    </div>
  );
};
